import { useEffect, useRef, useCallback } from 'react';
import { usePostHog } from '@posthog/react';

const IDLE_TIMEOUT_MS = 30_000;
const HEARTBEAT_INTERVAL_MS = 60_000;
const ACTIVITY_THROTTLE_MS = 1_000;

interface IdleState {
  isIdle: boolean;
  idleStartedAt: number | null;
}

/**
 * Fires a periodic `activity_heartbeat` event to PostHog while the drawing is
 * open: whether the SME was active, and how long the current idle period has run.
 *
 * Idle = 30s of no mouse/keyboard/scroll activity OR tab hidden.
 *
 * DECISION(2026-10-27, Abhishek): the heartbeat is the one timing event, and it
 * carries no step. The dashboard's step efficiency and idle-per-step charts read
 * `step_exited`, so they receive no new data from this release; a later PR removes
 * them. Active Minutes reads `is_active` and `idle_seconds_in_period` from here.
 */
export function useActivityHeartbeat(diagramId: string | null) {
  const posthog = usePostHog();
  const idleState = useRef<IdleState>({ isIdle: false, idleStartedAt: null });
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastThrottledActivity = useRef(0);
  const diagramRef = useRef(diagramId);

  diagramRef.current = diagramId;

  const markIdle = useCallback(() => {
    if (idleState.current.isIdle) return;
    idleState.current = { isIdle: true, idleStartedAt: Date.now() };
  }, []);

  const recordActivity = useCallback(() => {
    const now = Date.now();
    if (now - lastThrottledActivity.current < ACTIVITY_THROTTLE_MS) return;
    lastThrottledActivity.current = now;
    idleState.current = { isIdle: false, idleStartedAt: null };

    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(markIdle, IDLE_TIMEOUT_MS);
  }, [markIdle]);

  // Older builds registered the step as super-properties, which PostHog keeps on every
  // later event. Remove after 2027-10-27, when the 365-day PostHog persistence has expired.
  useEffect(() => {
    posthog?.unregister('step_number');
    posthog?.unregister('step_name');
  }, [posthog]);

  // Activity listeners
  useEffect(() => {
    const onActivity = () => recordActivity();
    const onVisibilityChange = () => {
      if (document.hidden) {
        markIdle();
      } else {
        recordActivity();
      }
    };

    window.addEventListener('mousemove', onActivity, { passive: true });
    window.addEventListener('keydown', onActivity, { passive: true });
    window.addEventListener('scroll', onActivity, { passive: true, capture: true });
    window.addEventListener('click', onActivity, { passive: true });
    document.addEventListener('visibilitychange', onVisibilityChange);

    idleTimer.current = setTimeout(markIdle, IDLE_TIMEOUT_MS);

    return () => {
      window.removeEventListener('mousemove', onActivity);
      window.removeEventListener('keydown', onActivity);
      window.removeEventListener('scroll', onActivity, { capture: true } as EventListenerOptions);
      window.removeEventListener('click', onActivity);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [recordActivity, markIdle]);

  // Heartbeat
  useEffect(() => {
    const heartbeatTimer = setInterval(() => {
      const { isIdle, idleStartedAt } = idleState.current;
      const periodIdleSeconds = isIdle && idleStartedAt
        ? (Date.now() - idleStartedAt) / 1000
        : 0;

      posthog?.capture('activity_heartbeat', {
        diagram_id: diagramRef.current,
        is_active: !isIdle,
        idle_seconds_in_period: Math.round(periodIdleSeconds),
      });
    }, HEARTBEAT_INTERVAL_MS);

    return () => clearInterval(heartbeatTimer);
  }, [posthog]);
}
